import React from 'react';
import { Button, Modal, Tag, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import DashboardWidgetPreview from './DashboardWidgetPreview';
import {
  DashboardWidgetDefinition,
  DashboardWidgetId,
  DashboardWidgetLayoutItem,
} from './dashboardWidgets';

const { Text } = Typography;

interface DashboardAddWidgetModalProps {
  open: boolean;
  // Every widget this role may pin, in default order.
  widgets: DashboardWidgetDefinition[];
  // Currently pinned layout; pinned widgets show as already on the panel.
  layout: DashboardWidgetLayoutItem[];
  onClose: () => void;
  onAdd: (id: DashboardWidgetId) => void;
}

// Each click adds one widget to the panel. The page decides whether that goes
// through edit mode (wide screens) or saves at once (narrow screens).
const DashboardAddWidgetModal: React.FC<DashboardAddWidgetModalProps> = ({
  open,
  widgets,
  layout,
  onClose,
  onAdd,
}) => {
  const { t } = useTranslation(['dashboard', 'common']);
  const pinnedIds = layout.map((item) => item.id);

  return (
    <Modal
      title={t('dashboard:customize.addTitle')}
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose}>{t('common:close')}</Button>}
      width={880}
      destroyOnHidden
    >
      <Text type="secondary">{t('dashboard:customize.addIntro')}</Text>
      <div className="dashboard-add-grid">
        {widgets.map((widget) => {
          const pinned = pinnedIds.includes(widget.id);
          return (
            <div key={widget.id} className="dashboard-add-card" data-testid={`dashboard-add-card-${widget.id}`}>
              <DashboardWidgetPreview id={widget.id} />
              <div className="dashboard-add-card__body">
                <strong>{t(widget.labelKey)}</strong>
                <Text type="secondary">{t(widget.descriptionKey)}</Text>
              </div>
              <div className="dashboard-add-card__footer">
                {pinned ? (
                  <Tag color="blue">{t('dashboard:customize.alreadyPinned')}</Tag>
                ) : (
                  <Button
                    size="small"
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => onAdd(widget.id)}
                  >
                    {t('dashboard:customize.add')}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

export default DashboardAddWidgetModal;
